// STARCODE - Three.js port of NEU.pde. Entry point: scene/renderer/composer setup, audio + MIDI
// bootstrapping, keyboard wiring and the main render loop.

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

import { ParticleSystem, PARTICLE_COUNT, BOUNDS_RADIUS } from "./ParticleSystem.js";
import { SunObject } from "./SunObject.js";
import { AudioAnalyser } from "./audio.js";
import { MidiManager } from "./midi.js";
import { AutoMix } from "./autoMix.js";
import { initRemoteSync, broadcastAction } from "./remoteSync.js";
import { installKeybinds, updateSizeCycle } from "./keybinds.js";
import { installControlBar } from "./controlBar.js";
import { state } from "./state.js";
import { initI18n, getLang, setLang, t } from "./i18n.js";
import { createAboutModal, showAboutModal } from "./aboutModal.js";

// Initialize internationalization & modals
initI18n();
createAboutModal();

const WORD = "STARCODE";

// --- Regie/Output split -----------------------------------------------------------------
// Live mode hides every button for a clean Syphon/OBS feed - which raises the obvious question:
// how do you drive the show if you can't see the buttons? The answer is to open this same page
// TWICE: one window as the clean picture (?role=output, fullscreen, captured), one window kept
// on your own screen as the control surface (?role=control, full button/slider UI, never hidden,
// never captured). remoteSync.js keeps both windows' `state` in sync via BroadcastChannel.
// Opened with no `role` param at all, it behaves exactly as a normal single-window instance.
const role = new URLSearchParams(location.search).get("role"); // "control" | "output" | null
const isControlRole = role === "control";
let exitRegieBtn = null;
document.body.classList.toggle("controlRole", isControlRole);
if (isControlRole) {
  document.title = `STARCODE — ${getLang() === "en" ? "Control" : "Regie"}`;
  exitRegieBtn = document.createElement("button");
  exitRegieBtn.type = "button";
  exitRegieBtn.className = "exitRegieBtn";
  exitRegieBtn.textContent = t("exitRegie");
  exitRegieBtn.title = t("exitRegieTip");
  exitRegieBtn.addEventListener("click", () => {
    const u = new URL(location.href);
    u.searchParams.delete("role");
    u.searchParams.delete("live");
    location.href = u.pathname;
  });
  document.body.appendChild(exitRegieBtn);
}
// Distinct title for the output role too, so it's easy to pick out by name in a Syphon/OBS
// "capture this specific window" source list (as opposed to "capture entire screen", which is
// exactly the approach that forces a fullscreen/single-window trade-off - see openOutputWindow
// below and applyLiveModeVisual, which deliberately does NOT force fullscreen anymore).
if (role === "output") document.title = "STARCODE — Output";

const canvas = document.getElementById("scene");
const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: "high-performance",
  preserveDrawingBuffer: true,
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.1;

const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x02030a, 0.00018);

const camera = new THREE.PerspectiveCamera(52, window.innerWidth / window.innerHeight, 0.5, 120000);
camera.position.set(0, 60, 780);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.06;
controls.autoRotate = true;
controls.autoRotateSpeed = 0.35;
controls.minDistance = 2;
controls.maxDistance = 100000;
// No angle limits at all: the camera may orbit a full 360 degrees horizontally and vertically,
// including straight over the poles. Only the zoom distance stays bounded.
controls.minAzimuthAngle = -Infinity;
controls.maxAzimuthAngle = Infinity;
controls.minPolarAngle = 0;
controls.maxPolarAngle = Math.PI;

// A faint starfield backdrop for depth/parallax - cheap (single Points draw call).
function buildStarfield() {
  const count = 3000;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = 1800 + Math.random() * 2200;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({ color: 0x4a5680, size: 2.2, sizeAttenuation: true, transparent: true, opacity: 0.5 });
  return new THREE.Points(geo, mat);
}
scene.add(buildStarfield());

const sunObject = new SunObject();
scene.add(sunObject.group);

const particleSystem = new ParticleSystem(renderer);
scene.add(particleSystem.points);

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.45, 0.35, 0.6);
composer.addPass(bloomPass);
composer.addPass(new OutputPass());

function onResize() {
  const winW = window.innerWidth;
  const winH = window.innerHeight;

  // Strict aspect ratio lock: prevents 3D stretching/distortion when integrated into Resolume,
  // OBS, or when resized on any monitor. The canvas always preserves the exact proportions of
  // the chosen format (e.g. 16:9 = 1.7777778), letterboxing/pillarboxing with black bars as needed.
  const targetW = state.outputWidth || 1920;
  const targetH = state.outputHeight || 1080;
  const targetAspect = targetW / targetH;
  const winAspect = winW / winH;

  let renderW, renderH;
  if (winAspect > targetAspect) {
    // Window is wider than target format -> Pillarbox (black bars on left & right)
    renderH = winH;
    renderW = Math.max(1, Math.round(winH * targetAspect));
  } else {
    // Window is taller than target format -> Letterbox (black bars on top & bottom)
    renderW = winW;
    renderH = Math.max(1, Math.round(winW / targetAspect));
  }

  // Camera projection is strictly locked to the target aspect ratio
  camera.aspect = targetAspect;
  camera.updateProjectionMatrix();

  renderer.setSize(renderW, renderH, false);
  composer.setSize(renderW, renderH);

  // Position canvas centered in window with fixed CSS dimensions
  renderer.domElement.style.width = `${renderW}px`;
  renderer.domElement.style.height = `${renderH}px`;
  renderer.domElement.style.position = "fixed";
  renderer.domElement.style.left = `${Math.round((winW - renderW) / 2)}px`;
  renderer.domElement.style.top = `${Math.round((winH - renderH) / 2)}px`;

  if (particleSystem.material?.uniforms?.uPixelRatio) {
    particleSystem.material.uniforms.uPixelRatio.value = Math.min(window.devicePixelRatio || 1, 2);
  }
}
window.addEventListener("resize", onResize);
onResize();

function resetCameraToSwarm() {
  camera.position.set(0, 60, 780);
  camera.up.set(0, 1, 0);
  controls.target.set(0, 0, 0);
  camera.lookAt(0, 0, 0);
  controls.update();
  if (state.autofocusOn) {
    state.focusDistance = 780;
  }
}

const audio = new AudioAnalyser();
const midi = new MidiManager({ particleSystem });
const autoMix = new AutoMix();
initRemoteSync({
  particleSystem,
  onAction: (action) => {
    if (action === "toggleFullscreen") toggleFullscreen();
    if (action === "resetCamera") resetCameraToSwarm();
  },
});

installKeybinds({
  particleSystem,
  onMorphToggle: (isOn) => {
    hudMorphFlash(isOn);
  },
});

// --- Live / Resolume output mode ------------------------------------------------------------
// A browser can't publish a Syphon/Spout/NDI sender itself, and Resolume has no screen-capture
// source - so the picture is handed over by a bridge app (Syphon publisher, OBS+NDI, or OBS's
// virtual camera) that captures this window. All this mode has to do is make that captured
// window CLEAN: no control bar, no HUD, no mouse cursor, and fullscreen on the output display.
// See README.md ("Live output to Resolume Arena") for the bridge setup.
//
// `state.liveModeOn` is just data and is shared with a control window via remoteSync - but only
// the OUTPUT window is allowed to act on it (hide its own UI). A control window must always keep
// its own buttons visible, even while it's driving the output into live mode.
//
// IMPORTANT: this deliberately does NOT force browser Fullscreen anymore. Fullscreen used to be
// requested automatically here, which is exactly the trap that broke single-window use: it takes
// over the ENTIRE display, so if the output and control UI are the same window, the buttons are
// gone with no way to get them back short of Escape. Hiding the control bar/HUD/cursor via the
// `liveMode` CSS class is the part that actually matters for a clean Syphon/OBS feed - a Syphon
// or OBS "capture this window" source grabs exactly that window's content whether it's fullscreen
// or just a normal window sized/positioned anywhere on screen. Use openOutputWindow() below to get
// a second, permanently clean window instead of fighting fullscreen in a single window.
function applyLiveModeVisual() {
  if (isControlRole) return;
  const on = state.liveModeOn;
  document.body.classList.toggle("liveMode", on);
}

function setLiveMode(on) {
  state.liveModeOn = on;
  applyLiveModeVisual();
}

function toggleLiveMode() {
  setLiveMode(!state.liveModeOn);
}

function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch((err) => {
      console.warn("[STARCODE] Fullscreen-Anfrage fehlgeschlagen:", err);
    });
  } else {
    document.exitFullscreen().catch(() => {});
  }
}

function handleFullscreenToggle() {
  if (isControlRole) {
    broadcastAction("toggleFullscreen");
  } else {
    toggleFullscreen();
  }
}

// Double-click on canvas toggles fullscreen in output/live view
renderer.domElement.addEventListener("dblclick", () => {
  toggleFullscreen();
});

window.addEventListener("keydown", (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  // Plain S only - Shift+S is a different binding (Gravity Wells, see keybinds.js) and must
  // NOT also flip live mode, or the control bar would silently reappear mid-show every time
  // that effect is toggled.
  if (!e.shiftKey && e.key.toLowerCase() === "s") toggleLiveMode();
  if (!e.shiftKey && (e.key.toLowerCase() === "f" || e.key === "F11")) toggleFullscreen();
  if (e.key === "0" || e.key === "Home") resetCameraToSwarm();
  if (e.key === "Escape") {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    if (state.liveModeOn) setLiveMode(false);
  }
});

// Explosion/RB-burst are one-shot particle-injection calls, not persistent state - so unlike
// every other button they can't just ride along on remoteSync's state-diff polling. A control
// window (no renderer of its own) broadcasts them explicitly instead of calling them locally.
function fireOneshot(action) {
  if (isControlRole) {
    broadcastAction(action, 180);
    return;
  }
  if (action === "explosion") particleSystem.triggerExplosion(90);
  if (action === "rbBurst") particleSystem.triggerRbBurst(90);
  if (action === "shockwave") particleSystem.triggerShockwave(180);
  if (action === "resetSwarm") particleSystem.resetSwarm();
}

// One-click setup for the Resolume/Syphon workflow:
// If supported (Google Chrome / Chromium), opens a native Document Picture-in-Picture window
// directly from the button: 100% frameless, ZERO address bar, ZERO tabs, pure clean picture!
// The main window automatically stays as the Regie (Control) surface with all buttons.
// Falls back to a clean standalone popup window if PiP is unavailable.
async function openOutputWindow() {
  const w = state.outputWidth || 1920;
  const h = state.outputHeight || 1080;

  if (window.documentPictureInPicture && typeof window.documentPictureInPicture.requestWindow === "function") {
    try {
      const pipWindow = await window.documentPictureInPicture.requestWindow({
        width: Math.min(w, window.screen.availWidth || 1920),
        height: Math.min(h, window.screen.availHeight || 1080),
      });

      pipWindow.document.body.style.margin = "0";
      pipWindow.document.body.style.padding = "0";
      pipWindow.document.body.style.overflow = "hidden";
      pipWindow.document.body.style.backgroundColor = "#000000";
      pipWindow.document.title = "STARCODE — Output";

      // Copy stylesheets
      [...document.querySelectorAll('link[rel="stylesheet"], style')].forEach((el) => {
        pipWindow.document.head.appendChild(el.cloneNode(true));
      });

      const origParent = canvas.parentNode;
      pipWindow.document.body.appendChild(canvas);
      canvas.style.position = "fixed";
      canvas.style.inset = "0";
      canvas.style.width = "100vw";
      canvas.style.height = "100vh";

      document.body.classList.add("controlRole");

      const onPipResize = () => {
        const pw = pipWindow.innerWidth;
        const ph = pipWindow.innerHeight;
        const targetAspect = (state.outputWidth || 1920) / (state.outputHeight || 1080);
        const winAspect = pw / ph;
        let rw, rh;
        if (winAspect > targetAspect) {
          rh = ph;
          rw = Math.max(1, Math.round(ph * targetAspect));
        } else {
          rw = pw;
          rh = Math.max(1, Math.round(pw / targetAspect));
        }
        camera.aspect = targetAspect;
        camera.updateProjectionMatrix();
        renderer.setSize(rw, rh, false);
        composer.setSize(rw, rh);
        canvas.style.width = `${rw}px`;
        canvas.style.height = `${rh}px`;
        canvas.style.left = `${Math.round((pw - rw) / 2)}px`;
        canvas.style.top = `${Math.round((ph - rh) / 2)}px`;
      };

      pipWindow.addEventListener("resize", onPipResize);
      onPipResize();

      pipWindow.addEventListener("pagehide", () => {
        origParent.appendChild(canvas);
        document.body.classList.remove("controlRole");
        window.dispatchEvent(new Event("resize"));
      });

      return;
    } catch (err) {
      console.warn("[STARCODE] Document PiP konnte nicht geöffnet werden, nutze Popup-Fallback:", err);
    }
  }

  // Fallback: standalone popup window
  const left = Math.max(0, Math.round((window.screen.width - w) / 2));
  const top = Math.max(0, Math.round((window.screen.height - h) / 2));
  const outUrl = new URL(location.href);
  outUrl.searchParams.set("role", "output");
  outUrl.searchParams.set("live", "1");
  const win = window.open(
    outUrl.toString(),
    "starcodeOutput",
    `popup=yes,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes,width=${w},height=${h},left=${left},top=${top}`
  );
  if (!win) {
    window.alert(
      "Das Ausgabe-Fenster wurde vom Popup-Blocker verhindert.\n\n" +
        "Bitte Popups für diese Seite erlauben und erneut klicken."
    );
    return;
  }
  win.focus();

  // Avoid two live copies of the renderer fighting for the GPU - this window switches to being
  // pure control surface once the dedicated output window exists.
  if (!isControlRole) {
    const selfUrl = new URL(location.href);
    selfUrl.searchParams.set("role", "control");
    selfUrl.searchParams.delete("live");
    location.href = selfUrl.toString();
  }
}

// On-screen buttons - the reliable, interference-free way to trigger every effect (some keyboard
// shortcuts collide with browser/OS-reserved keys depending on layout, buttons never do).
// MIDI stays owned by the output window only (see midi.start() below) - a control window skips
// the MIDI-Learn section entirely so the same controller input can't get double-processed.
const syncControlBar = installControlBar({
  particleSystem,
  midi: isControlRole ? null : midi,
  onLiveModeToggle: toggleLiveMode,
  onFullscreenToggle: handleFullscreenToggle,
  onResetCamera: () => {
    resetCameraToSwarm();
    broadcastAction("resetCamera");
  },
  onOpenOutput: openOutputWindow,
  onOneshot: fireOneshot,
  onObjLoaded: (objText, filename) => {
    return particleSystem.loadCustomObj(objText, filename);
  },
  onMorphToggle: (isOn) => {
    hudMorphFlash(isOn);
  },
});

// --- Classic keyboard zoom (scroll wheel already zooms via OrbitControls by default; this adds
// a "+"/"-" hold-to-zoom control on top, closer to the U/Z zoom keys in NEU.pde). ---
let zoomInHeld = false;
let zoomOutHeld = false;
window.addEventListener("keydown", (e) => {
  if (e.key === "+" || e.key === "=") zoomInHeld = true;
  if (e.key === "-" || e.key === "_") zoomOutHeld = true;
});
window.addEventListener("keyup", (e) => {
  if (e.key === "+" || e.key === "=") zoomInHeld = false;
  if (e.key === "-" || e.key === "_") zoomOutHeld = false;
});
function applyKeyboardZoom(dt) {
  if (!zoomInHeld && !zoomOutHeld) return;
  const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
  const dist = offset.length();
  const speed = Math.max(160, dist * 1.4) * dt;
  const delta = zoomInHeld ? -speed : speed;
  const newDist = THREE.MathUtils.clamp(dist + delta, controls.minDistance, controls.maxDistance);
  offset.setLength(newDist);
  camera.position.copy(controls.target).add(offset);
}

// --- Hold "1" + mouse-drag => pure X/Y pan (no rotation)
// Hold "2" + mouse-drag up/down => zoom (tap "2" toggles Scale/Freq.)
// Hold "3" + mouse-drag => unrestricted 360° spin in all 3D directions (tap "3" toggles 360 Spin auto-rotation)
let threeHeld = false;
let twoHeld = false;
let oneHeld = false;
window.addEventListener("keydown", (e) => {
  if (e.key === "3") {
    threeHeld = true;
    document.getElementById("navBadge3")?.classList.add("active");
  }
  if (e.key === "2") {
    twoHeld = true;
    document.getElementById("navBadge2")?.classList.add("active");
  }
  if (e.key === "1") {
    oneHeld = true;
    document.getElementById("navBadge1")?.classList.add("active");
  }
});
window.addEventListener("keyup", (e) => {
  if (e.key === "3") {
    threeHeld = false;
    document.getElementById("navBadge3")?.classList.remove("active");
  }
  if (e.key === "2") {
    twoHeld = false;
    document.getElementById("navBadge2")?.classList.remove("active");
  }
  if (e.key === "1") {
    oneHeld = false;
    document.getElementById("navBadge1")?.classList.remove("active");
  }
});

let dragMode = null; // null | "zoom" | "pan" | "spin"
let dragLastX = 0;
let dragLastY = 0;
let dragWasAutoRotate = false;

function onDragPointerDown(e) {
  if (!threeHeld && !twoHeld && !oneHeld) return;
  dragMode = threeHeld ? "spin" : twoHeld ? "zoom" : "pan";
  dragLastX = e.clientX;
  dragLastY = e.clientY;
  dragWasAutoRotate = controls.autoRotate;
  controls.autoRotate = false;
  controls.enabled = false;
  e.preventDefault();
}

function onDragPointerMove(e) {
  if (!dragMode) return;
  const dx = e.clientX - dragLastX;
  const dy = e.clientY - dragLastY;
  dragLastX = e.clientX;
  dragLastY = e.clientY;

  if (dragMode === "spin") {
    // Completely unconstrained 3D orbital spin. Rotating around the camera's own right/up axes
    // (instead of spherical theta/phi) means there are no poles to clamp against, so the drag
    // can tumble endlessly in every direction. camera.up is carried along so OrbitControls
    // continues from the new orientation without snapping back.
    const rotSpeed = 0.007;
    const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    camera.matrixWorld.extractBasis(right, up, new THREE.Vector3());
    const rot = new THREE.Quaternion()
      .setFromAxisAngle(up, -dx * rotSpeed)
      .multiply(new THREE.Quaternion().setFromAxisAngle(right, -dy * rotSpeed));
    offset.applyQuaternion(rot);
    camera.up.applyQuaternion(rot).normalize();
    camera.position.copy(controls.target).add(offset);
    camera.lookAt(controls.target);
  } else if (dragMode === "zoom") {
    const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
    const dist = offset.length();
    const newDist = THREE.MathUtils.clamp(dist + dy * dist * 0.0035, controls.minDistance, controls.maxDistance);
    offset.setLength(newDist);
    camera.position.copy(controls.target).add(offset);
  } else if (dragMode === "pan") {
    const dist = camera.position.distanceTo(controls.target);
    const panSpeed = dist * 0.0016;
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    const forward = new THREE.Vector3();
    camera.matrixWorld.extractBasis(right, up, forward);
    const shift = right.multiplyScalar(-dx * panSpeed).add(up.multiplyScalar(dy * panSpeed));
    camera.position.add(shift);
    controls.target.add(shift);
  }
}

function onDragPointerUp() {
  if (!dragMode) return;
  dragMode = null;
  controls.enabled = true;
  controls.autoRotate = dragWasAutoRotate;
}

// Capture phase so this runs (and can disable `controls`) before OrbitControls' own listener.
renderer.domElement.addEventListener("pointerdown", onDragPointerDown, { capture: true });
window.addEventListener("pointermove", onDragPointerMove);
window.addEventListener("pointerup", onDragPointerUp);

// --- HUD & Top Navigation ---------------------------------------------------
const fpsEl = document.getElementById("fps");
const particleCountEl = document.getElementById("particleCount");
const audioStateEl = document.getElementById("audioState");
const midiStateEl = document.getElementById("midiState");
const beatStateEl = document.getElementById("beatState");
const sceneStateEl = document.getElementById("sceneState");
const liveStateEl = document.getElementById("liveState");

function updateParticleCountDisplay() {
  if (particleCountEl) {
    particleCountEl.textContent = `${PARTICLE_COUNT.toLocaleString(getLang() === "de" ? "de-DE" : "en-US")} ${t("particles")}`;
  }
}
updateParticleCountDisplay();

function syncLanguageButtons() {
  const cur = getLang();
  document.querySelectorAll(".langBtn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.lang === cur);
  });
}

document.querySelectorAll(".langBtn").forEach((btn) => {
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    setLang(btn.dataset.lang);
  });
});

const startAboutBtn = document.getElementById("startAboutBtn");
if (startAboutBtn) {
  startAboutBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    showAboutModal("about");
  });
}

const hudBrand = document.getElementById("hudBrand");
if (hudBrand) {
  hudBrand.addEventListener("click", (e) => {
    e.stopPropagation();
    showAboutModal("about");
  });
}

const hudAboutBtn = document.getElementById("hudAboutBtn");
if (hudAboutBtn) {
  hudAboutBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    showAboutModal("about");
  });
}

window.addEventListener("starcode:langchange", () => {
  syncLanguageButtons();
  updateParticleCountDisplay();
  if (isControlRole) {
    document.title = `STARCODE — ${getLang() === "en" ? "Control" : "Regie"}`;
    if (exitRegieBtn) {
      exitRegieBtn.textContent = t("exitRegieBtn");
      exitRegieBtn.title = t("exitRegieTitle");
    }
  }
});
syncLanguageButtons();

function hudMorphFlash(isOn) {
  document.title = isOn ? "STARCODE ✦" : "STARCODE";
}

let fpsFrames = 0;
let fpsAccum = 0;

function updateHud(dt) {
  fpsFrames++;
  fpsAccum += dt;
  if (fpsAccum >= 0.5) {
    fpsEl.textContent = `${Math.round(fpsFrames / fpsAccum)} ${t("fps")}`;
    fpsFrames = 0;
    fpsAccum = 0;
  }
  if (isControlRole) {
    audioStateEl.textContent = t("regieWindow");
  } else {
    const src = audio.usingMic ? (getLang() === "en" ? "Mic" : "Mikrofon") : (getLang() === "en" ? "Synthetic" : "synthetisch");
    const bars = Math.round(Math.min(1.0, audio.level) * 10);
    const meter = "█".repeat(bars) + "·".repeat(10 - bars);
    audioStateEl.textContent = `Audio: ${src} [${meter}]`;
  }
  midiStateEl.textContent = state.midiConnected ? t("midiConnected") : "";
  beatStateEl.textContent = audio.bpm ? `~${audio.bpm} BPM` : "";
  sceneStateEl.textContent = state.autoMixOn && autoMix.currentSceneName ? `${t("autoMixPrefix")}${autoMix.currentSceneName}` : "";
  liveStateEl.textContent = state.liveModeOn ? t("liveBadge") : "";
}

// --- Start overlay / bootstrap ---------------------------------------------
const startOverlay = document.getElementById("startOverlay");
const startButton = document.getElementById("startButton");

let isBooting = false;

// MediaStream Keep-Alive Pipeline: prevents Chrome / macOS from freezing the window or Syphon frame when Resolume is in focus
function startCanvasMediaKeepAlive() {
  try {
    if (!canvas || typeof canvas.captureStream !== "function") return;
    const stream = canvas.captureStream(60);
    const video = document.createElement("video");
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.srcObject = stream;
    video.style.position = "fixed";
    video.style.width = "2px";
    video.style.height = "2px";
    video.style.opacity = "0.001";
    video.style.pointerEvents = "none";
    video.style.zIndex = "-9999";
    document.body.appendChild(video);
    video.play().catch(() => {});
  } catch (e) {
    console.warn("[STARCODE] Media stream keepalive warning:", e);
  }
}

async function boot() {
  if (isBooting || state.audioReady) return;
  isBooting = true;
  if (role === "output" || new URLSearchParams(location.search).get("live") === "1") {
    startOverlay.classList.add("hidden");
    setLiveMode(true);
  }
  // A control window never renders and never needs its own mic/MIDI access - it only drives the
  // output window's state via remoteSync - so it can skip straight past all of that and boot
  // near-instantly (also means no separate microphone-permission prompt on that machine/window).
  const tasks = isControlRole
    ? []
    : [audio.start(), midi.start(), particleSystem.loadMorphTarget(WORD)];
  await Promise.all(tasks);
  state.audioReady = true;
  startOverlay.classList.add("hidden");
  startCanvasMediaKeepAlive();

  // ?live=1 boots straight into clean output - handy for a dedicated Resolume capture window
  // that should never show UI, even for a frame.
  if (new URLSearchParams(location.search).get("live") === "1") setLiveMode(true);
}

startButton.addEventListener("click", () => {
  boot();
});

// Auto-boot if opened as dedicated output window or with ?live=1 so Resolume / Syphon
// captures live visuals immediately without waiting for extra clicks.
if (role === "output" || new URLSearchParams(location.search).get("live") === "1") {
  startOverlay.classList.add("hidden");
  setLiveMode(true);
  boot();
}

// --- Main loop & background-safe render clock --------------------------------
// Standard requestAnimationFrame is throttled down or paused by macOS / Chromium when a window is
// not in the foreground (e.g. while Resolume Arena or OBS is the active focused application).
// We combine continuous requestAnimationFrame with an AudioContext / Worker interval heartbeat
// to ensure rendering NEVER drops below 60-120 fps even when completely in the background.
let lastFrameTime = performance.now();
let currentAudioRotSpeed = 0.0;
const clock = new THREE.Clock();

function renderFrame() {
  const now = performance.now();
  const elapsed = (now - lastFrameTime) / 1000;
  if (elapsed < 0.007) return; // Cap at ~120 fps
  lastFrameTime = now;
  const dt = Math.min(elapsed, 0.05);

  // The control window has no visible canvas at all (see style.css `.controlRole #scene`), so
  // skip every bit of GPGPU simulation and rendering work for it entirely - it's pure UI, driving
  // the actual output window over remoteSync instead.
  if (!isControlRole) {
    audio.update(dt);
    autoMix.update(dt, audio);
    updateSizeCycle(dt);

    const isSunActive = !!(state.sunOn || state.fireballOn);
    const currentSunScale = sunObject.update(dt, audio, isSunActive, camera);
    particleSystem.setSunScale(currentSunScale);
    particleSystem.update(dt, audio);

    // --- Refined, Smooth & Musical Audio-Reactive Camera Rotation ---
    // Instead of harsh whipping, this uses continuous momentum and scales smoothly with the slider:
    // - Silence / quiet: rotation smoothly glides to a stop (0.0).
    // - In rhythm: gentle, elegant rotation scaling with volume and beat.
    // - On kicks / drops: a subtle, smooth acceleration bump (never dizzying or disorienting).
    let targetRotSpeed = state.rotationSpeed;
    if (state.rotationAudioReact) {
      const gain = state.audioGain ?? 1.0;
      const userSlider = Math.max(0.05, state.rotationSpeed);
      const audioEnergy = (audio.bass * 0.5 + audio.low * 0.3 + audio.flux * 0.3 + audio.level * 0.4) * gain;
      const noiseThreshold = 0.04;

      let targetSpeed = 0.0;
      if (audioEnergy > noiseThreshold) {
        const normEnergy = Math.min(1.5, (audioEnergy - noiseThreshold) / (1.0 - noiseThreshold));
        // Groove baseline speed (smooth proportional curve)
        const groove = Math.pow(normEnergy, 1.1) * userSlider * 1.1;
        // Subtle beat nudge on kicks (+40% max)
        const beatNudge = (audio.beatPulse * 0.4 + (audio.beat ? 0.35 : 0.0)) * userSlider;
        targetSpeed = groove + beatNudge;
      }

      // Smooth momentum easing: fast gentle rise (attack), soft cinematic glide down (release)
      const easeRate = targetSpeed > currentAudioRotSpeed ? 5.0 : 2.2;
      currentAudioRotSpeed += (targetSpeed - currentAudioRotSpeed) * Math.min(1.0, dt * easeRate);
      targetRotSpeed = currentAudioRotSpeed;
    }

    if (state.spin360On) {
      controls.autoRotate = state.rotationAudioReact ? (targetRotSpeed > 0.005) : true;
      controls.autoRotateSpeed = state.rotationAudioReact ? targetRotSpeed : (state.rotationSpeed > 0 ? state.rotationSpeed * 1.5 : 0.8);
    } else {
      controls.autoRotate = state.rotationAudioReact ? (targetRotSpeed > 0.005) : (state.rotationSpeed > 0.001);
      controls.autoRotateSpeed = targetRotSpeed;
    }

    // Bloom is a live-performance parameter (slider + MIDI-assignable), so push it every frame.
    bloomPass.strength = state.bloomAmount;

    // Autofocus: dynamically track camera-to-subject focal distance in real time
    if (state.autofocusOn) {
      const targetDist = camera.position.distanceTo(controls.target);
      state.focusDistance += (targetDist - state.focusDistance) * Math.min(1, dt * 6.0);
    }

    applyKeyboardZoom(dt);
    controls.update();
    composer.render();
  }

  // Runs on both roles: reacts to state.liveModeOn regardless of whether it changed locally or
  // arrived from the other window over remoteSync.
  applyLiveModeVisual();
  updateHud(dt);
  syncControlBar();
}

function animateRAF() {
  requestAnimationFrame(animateRAF);
  renderFrame();
}
requestAnimationFrame(animateRAF);

// Background Web Worker Ticker (unthrottled by browser tab occlusion / background state)
try {
  const tickerCode = `
    let timer = null;
    self.onmessage = (e) => {
      if (e.data === "start") {
        if (!timer) timer = setInterval(() => self.postMessage("tick"), 1000 / 120);
      } else if (e.data === "stop") {
        clearInterval(timer);
        timer = null;
      }
    };
  `;
  const blob = new Blob([tickerCode], { type: "application/javascript" });
  const tickerWorker = new Worker(URL.createObjectURL(blob));
  tickerWorker.onmessage = () => {
    renderFrame();
  };
  tickerWorker.postMessage("start");
} catch (err) {
  console.warn("[STARCODE] Worker ticker unavailable:", err);
}

// Additional AudioContext ScriptProcessor / timer heartbeat for background keep-alive
try {
  const silentCtx = new (window.AudioContext || window.webkitAudioContext)();
  const osc = silentCtx.createOscillator();
  const gain = silentCtx.createGain();
  gain.gain.value = 0; // completely silent
  osc.connect(gain);
  gain.connect(silentCtx.destination);
  osc.start();
  setInterval(() => {
    if (silentCtx.state === "suspended") silentCtx.resume().catch(() => {});
    renderFrame();
  }, 1000 / 60);
} catch (_) {}
